import { useState, useEffect } from 'react';
import { saveConfig } from '../utils/api';

interface SettingsProps {
  onSuccess: (message: string) => void;
  alertsEnabled: boolean;
  setAlertsEnabled: (enabled: boolean) => void;
}

export function Settings({
  onSuccess,
  alertsEnabled,
  setAlertsEnabled,
}: SettingsProps) {
  const [baseUrl, setBaseUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [digestEnabled, setDigestEnabled] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('mailtracker_config');
    if (stored) {
      const config = JSON.parse(stored);
      setBaseUrl(config.baseUrl || '');
      setClientId(config.clientId || '');
      setClientSecret(config.clientSecret || '');
    }

    const digestSetting = localStorage.getItem('mailtracker_digest');
    setDigestEnabled(digestSetting !== 'false');
  }, []);

  const handleSave = () => {
    saveConfig({ baseUrl, clientId, clientSecret });
    localStorage.setItem('mailtracker_digest', String(digestEnabled));
    onSuccess('Settings saved!');
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setAlertsEnabled(true);
        onSuccess('Notifications enabled!');
      }
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>Settings</h2>
      </div>
      <div className="card-body">
        <div className="input-group">
          <label>API Base URL</label>
          <input
            type="url"
            className="input"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://mail.truebrewbirdie.com"
          />
          <small style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
            Leave empty for same-origin (local dev with proxy)
          </small>
        </div>

        <div className="input-group">
          <label>CF Access Client ID</label>
          <input
            type="text"
            className="input"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="Your Cloudflare Access Client ID"
          />
        </div>

        <div className="input-group">
          <label>CF Access Client Secret</label>
          <input
            type="password"
            className="input"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder="Your Cloudflare Access Client Secret"
          />
        </div>

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--gray-200)',
            margin: '1.5rem 0',
          }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <div>
            <strong>Browser Notifications</strong>
            <p
              style={{
                color: 'var(--gray-500)',
                fontSize: '0.875rem',
                margin: 0,
              }}
            >
              Get notified when emails are opened
            </p>
          </div>
          {!alertsEnabled &&
          'Notification' in window &&
          Notification.permission !== 'granted' ? (
            <button
              className="btn btn-secondary btn-sm"
              onClick={requestNotificationPermission}
            >
              Enable
            </button>
          ) : (
            <label className="toggle">
              <input
                type="checkbox"
                checked={alertsEnabled}
                onChange={(e) => setAlertsEnabled(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <div>
            <strong>Daily Digest Email</strong>
            <p
              style={{
                color: 'var(--gray-500)',
                fontSize: '0.875rem',
                margin: 0,
              }}
            >
              Receive a daily summary of opens (configured on server)
            </p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={digestEnabled}
              onChange={(e) => setDigestEnabled(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <hr
          style={{
            border: 'none',
            borderTop: '1px solid var(--gray-200)',
            margin: '1.5rem 0',
          }}
        />

        <button className="btn btn-primary" onClick={handleSave}>
          Save Settings
        </button>
      </div>
    </div>
  );
}
